/**
 * Tiers de licencia — reglas puras (OT-0008 §4.2).
 *
 * Espejo en frontend de `app/core/permissions.py`. **No sustituye al
 * backend**: el enforcement real vive allá, y filtrar en la UI no es
 * enforcement. Esto existe para decidir qué se muestra y qué se
 * habilita, no para autorizar.
 *
 * Vive aquí, y no repartido por las vistas, por la misma razón que en
 * backend: hoy `me.role === 'tenant_admin'` aparecía copiado en tres
 * pantallas, y cambiar la regla obligaba a encontrarlas todas.
 */

import type { MeResponse, UserTenantRole } from '../api/me'
import type { TenantAccess } from '../api/platform'

/** Los cuatro tiers, en orden de capacidad descendente. */
export const TIERS: readonly UserTenantRole[] = ['admin', 'editor', 'reviewer', 'external']

/** Tiers que pueden producir contenido. Invariante D12: solo estos
 *  pueden ser dueños de un artefacto, y por tanto editarlo. */
const EDITING_TIERS: readonly UserTenantRole[] = ['admin', 'editor']

/** Tiers internos del proveedor: leen los recursos internos del tenant. */
const INTERNAL_TIERS: readonly UserTenantRole[] = ['admin', 'editor', 'reviewer']

export function isKnownTier(value: string): value is UserTenantRole {
  return (TIERS as readonly string[]).includes(value)
}

/**
 * Tier tal como llega de la API. `null` es un valor real del contrato:
 * un miembro externo invitado por email todavía no tiene usuario en el
 * tenant. Los predicados lo aceptan y responden `false` — un tier que
 * no conocemos no habilita nada (regla 2 de `permissions.py`: deny by
 * default).
 */
export type MaybeTier = UserTenantRole | null | undefined

export function isAdmin(role: MaybeTier): boolean {
  return role === 'admin'
}

/** Acceso de la organización tal como llega en `/me` (`tenant.access`). Sin
 *  tenant (usuario en limbo) no hay acceso que habilite nada. */
export type MaybeAccess = TenantAccess | null | undefined

/**
 * Espejo de `require_tenant_writable` (`app/core/permissions.py`, OT-0072):
 * la organización permite crear o modificar sólo con acceso `full`. En
 * `read_only` (beta vencida) se consulta todo pero no se escribe; en
 * `suspended` no se opera (OT-0073 §4.4).
 *
 * Todo predicado de escritura de este archivo lo compone, así cada pantalla
 * que ya pregunta "¿puede editar/crear/gestionar?" respeta el estado de la
 * organización sin repetir la regla.
 */
export function canWriteInTenant(access: MaybeAccess): boolean {
  return access === 'full'
}

/**
 * Espejo de `can_manage_tenant` en una escritura: administrar la
 * organización (invitar, plantillas, metodologías, base de conocimiento,
 * borrar proyectos, agentes del tenant). `isAdmin` sigue siendo la pregunta
 * de lectura ("¿ve lo de admin?", p. ej. los totales de consumo).
 */
export function canManageTenant(role: MaybeTier, access: MaybeAccess): boolean {
  return tenantManageBlock(role, access) === null
}

/** Por qué no se puede administrar la organización, para explicarlo en la
 *  UI (p. ej. el diseñador de plantillas en solo lectura); `null` si se puede. */
export type TenantManageBlock = 'not_admin' | 'organization_read_only'

export function tenantManageBlock(role: MaybeTier, access: MaybeAccess): TenantManageBlock | null {
  if (!isAdmin(role)) return 'not_admin'
  if (!canWriteInTenant(access)) return 'organization_read_only'
  return null
}

export function isExternal(role: MaybeTier): boolean {
  return role === 'external'
}

/**
 * Espejo de `can_read_tenant_internal_resources` (`app/core/permissions.py`,
 * OT-0015): catálogo de plantillas (con su reporte de calidad), metodologías,
 * padrón de usuarios, KPIs y base de conocimiento del tenant.
 *
 * Backend lo escribe como `role is not EXTERNAL`; aquí es una lista blanca.
 * Para los cuatro tiers vigentes ambas formas responden lo mismo. La
 * diferencia es deliberada para valores que este bundle no conoce (`null`,
 * `undefined` o un tier nuevo): el cliente los rechaza en vez de habilitar
 * pantallas por omisión. No cambia la política, que sigue siendo la del
 * backend.
 */
export function canReadTenantInternalResources(role: MaybeTier): boolean {
  return role != null && (INTERNAL_TIERS as readonly string[]).includes(role)
}

/**
 * Techo por tier para listar proyectos y entrar a uno — espejo de
 * `project_visibility_clause` y `can_view_project`: el `external` nunca
 * lista ni entra a un proyecto; solo ve artefactos sueltos donde es
 * aprobador (Doc 29 §2.1, §6.2). Qué proyectos concretos ve un tier
 * interno lo decide la membresía en el backend, no esto.
 */
export function canAccessProjects(role: MaybeTier): boolean {
  return role != null && (INTERNAL_TIERS as readonly string[]).includes(role)
}

/**
 * Espejo del techo por tier de `can_view_project_internals`: fuentes, base de
 * conocimiento y consumo de IA del proyecto, y lo que la IA derivó de ellos
 * (la verificación por oración de OT-0049). El `external` no los ve
 * (Doc 29 §6.2); la membresía la sigue decidiendo el backend.
 */
export function canViewProjectInternals(role: MaybeTier): boolean {
  return role != null && (INTERNAL_TIERS as readonly string[]).includes(role)
}

/**
 * Consumo y costos de IA (Doc 29 §2.1, §6.2): ningún tier interno queda
 * fuera; el `external` no. El backend de `/usage` no lo rechaza —filtra
 * al consumo propio, que para un `external` es nada—, así que esto es la
 * regla del modelo de permisos aplicada a la pantalla, no un espejo de un
 * guard.
 */
export function canViewAiUsage(role: MaybeTier): boolean {
  return role != null && (INTERNAL_TIERS as readonly string[]).includes(role)
}

/** Operador de la plataforma HIVA, ortogonal al tier del tenant. Espejo de
 *  `require_platform_admin`. */
export function isPlatformAdmin(me: Pick<MeResponse, 'is_platform_admin'> | undefined): boolean {
  return me?.is_platform_admin === true
}

/** D12 — solo `admin` o `editor` pueden ser responsables de un
 *  documento. Es lo que filtra el picker de "Responsable": mostrar a un
 *  reviewer ahí sería ofrecer algo que el backend va a rechazar. */
export function canOwnArtifact(role: MaybeTier): boolean {
  return role != null && (EDITING_TIERS as readonly string[]).includes(role)
}

/** Acceso de agentes de IA por MCP (OT-0043, Docs/15 §3.3) — espejo de
 *  `can_use_agent_access`: sólo tiers de pago; el `external` no. */
export function canUseAgentAccess(role: MaybeTier): boolean {
  return role != null && role !== 'external'
}

/** D17 — crear proyecto, quedando como líder. */
export function canCreateProject(role: MaybeTier, access: MaybeAccess): boolean {
  return canOwnArtifact(role) && canWriteInTenant(access)
}

/** "Edito documentos en esta organización": tier de edición (D12) y la
 *  organización permite escribir. Separa en la UI el "tu tier no edita"
 *  (se oculta "Editar") del "tu tier edita, pero no este documento"
 *  (deshabilitado con explicación). En sólo lectura cae en lo primero. */
export function canEditSomeArtifact(role: MaybeTier, access: MaybeAccess): boolean {
  return canOwnArtifact(role) && canWriteInTenant(access)
}

/** Conectar un agente nuevo (OT-0043) es escribir: en sólo lectura el
 *  backend lo niega (OT-0072 §4.2). Ver los agentes ya conectados no. */
export function canConnectAgent(role: MaybeTier, access: MaybeAccess): boolean {
  return canUseAgentAccess(role) && canWriteInTenant(access)
}

/** Revocar una conexión de agente o rechazar una solicitud sólo QUITAN
 *  acceso: también en sólo lectura, para poder cortárselo a un agente que
 *  sigue leyendo por MCP. Espejo de `READ_ONLY_SAFE_ROUTES` (OT-0073,
 *  decisión de Ricardo); aprobar sigue en `canConnectAgent`. */
export function canRevokeAgentAccess(role: MaybeTier): boolean {
  return canUseAgentAccess(role)
}

/** Relación del usuario con un proyecto que deciden los predicados de
 *  producir contenido (OT-0058 §4.5). */
export interface ProjectMembershipContext {
  /** `true` si el usuario es `project_members` de este proyecto. */
  isMember: boolean
  /** Su sección en el proyecto; `null` si no es miembro. */
  section: TeamSection | null
}

/** Espejo de `can_produce_in_project` (OT-0058 §4.5, licencia ∩ sección):
 *  crear y editar documentos y subir fuentes. Tier de edición (D12) y, si
 *  es miembro, estar en el equipo operativo. El admin que no es miembro
 *  conserva su acceso transversal; en el extendido solo revisa. Ver todos
 *  los proyectos (`can_view_all_projects`) no es participar. */
export function canProduceInProject(
  role: MaybeTier,
  access: MaybeAccess,
  ctx: ProjectMembershipContext,
): boolean {
  if (!canWriteInTenant(access)) return false
  if (!canOwnArtifact(role)) return false
  if (!ctx.isMember) return isAdmin(role)
  return ctx.section === 'operational'
}

/** Contexto ya resuelto (una vez por página, nunca por fila — OT-0009
 *  §4.3) que necesitan los predicados de acceso a un artifact concreto. */
export interface ArtifactAccessContext extends ProjectMembershipContext {
  /** `true` si es el líder activo del proyecto (`project.lead_member_id`). */
  isLead: boolean
  /** `true` si `artifact.owner_user_id === me.id`. */
  isOwner: boolean
}

/** D11 — espejo de `can_edit_artifact`: dueño ∪ líder ∪ admin, y solo si
 *  además puede producir en el proyecto (`canProduceInProject`). Un
 *  `reviewer` no edita nunca, ni un editor en el equipo extendido. Ofrecer
 *  "Editar" fuera de esto es exactamente el bug que cierra la OT-0009: el
 *  backend lo rechaza con 403 después de que el usuario ya escribió. */
export function canEditArtifact(
  role: MaybeTier,
  access: MaybeAccess,
  ctx: ArtifactAccessContext,
): boolean {
  if (!canProduceInProject(role, access, ctx)) return false
  if (isAdmin(role)) return true
  return ctx.isOwner || ctx.isLead
}

/**
 * Espejo de `POST /projects/{id}/artifacts` (`require_content_production`):
 * crear un documento es producir contenido en el proyecto
 * (`canProduceInProject`). Desde OT-0058 un editor que solo ve el proyecto
 * por `can_view_all_projects`, o que está en el equipo extendido, no crea:
 * misma regla que editar y subir fuentes.
 */
export function canCreateArtifact(
  role: MaybeTier,
  access: MaybeAccess,
  ctx: ProjectMembershipContext,
): boolean {
  return canProduceInProject(role, access, ctx)
}

/**
 * Espejo de `can_assign_owner` / `can_manage_team` — mismo cuerpo en
 * backend (admin ∪ (miembro ∧ líder)), así que es un solo predicado
 * aquí también: asignar responsable de fase, añadir/editar/quitar
 * miembro del equipo y transferir liderazgo son la misma pregunta de
 * acceso ("¿esta persona conduce el proyecto?"), no cuatro reglas
 * distintas. Si backend alguna vez separa esos dos criterios, un solo
 * predicado frontend dejará de alcanzar para los dos casos y esa grieta
 * se nota de inmediato.
 */
export function canManageTeam(
  role: MaybeTier,
  access: MaybeAccess,
  ctx: { isMember: boolean; isLead: boolean },
): boolean {
  if (!canWriteInTenant(access)) return false
  if (isAdmin(role)) return true
  return ctx.isMember && ctx.isLead
}

/**
 * Espejo de `can_view_artifact` para un artefacto en `draft`.
 *
 * Backend: para tier interno, `can_view_artifact` devuelve exactamente
 * `can_view_project(user, access)` — los borradores incluidos, porque
 * dentro del equipo la transparencia es sana. Como cualquier pantalla
 * que ofrezca esta lectura ya pasó `require_project_view`, el tier
 * interno que llega hasta aquí satisface el predicado por construcción
 * y basta con excluir a `external`, para quien el backend exige
 * `artifact.is_approver` — un dato que no llega a estos componentes y
 * que un draft sin ciclo de revisión no tiene.
 *
 * OT-0009 M-A — separa LEER de EDITAR: `canEditArtifact` decide si se
 * ofrece "Editar"; este decide si se ofrece "Ver". Ocultar los dos a la
 * vez dejaba al `reviewer` miembro con una capacidad declarada
 * (`canCommentOnArtifact`) pero inalcanzable.
 */
export function canViewDraft(role: MaybeTier): boolean {
  return role != null && !isExternal(role)
}

/**
 * Espejo de `can_comment_on_artifact` — solo para tiers internos.
 *
 * Backend: `is_tenant_admin(user) or access.is_member`, sin exigir tier
 * de edición (un `reviewer` miembro sí comenta, a diferencia de
 * `canManageProjectSources`/`canCreateArtifact`). Ver el proyecto por
 * `can_view_all_projects` sin ser miembro NO alcanza — comentar es
 * participar, no solo mirar.
 *
 * OT-0009 M4 — para `external` el backend exige ser
 * `artifact.is_approver`, un dato que hoy no llega a los componentes de
 * comentarios (`CommentsPanel`/`CommentsViewerPanel` no consultan
 * approvals). Se deja sin gatear a propósito: ocultar sin ese dato
 * bloquearía a un aprobador legítimo (falso negativo), y no es peor que
 * el estado actual. Gap conocido, no una aproximación silenciosa.
 */
export function canCommentOnArtifact(
  role: MaybeTier,
  access: MaybeAccess,
  ctx: { isMember: boolean },
): boolean {
  if (role == null || !canWriteInTenant(access)) return false
  if (isExternal(role)) return true
  return isAdmin(role) || ctx.isMember
}

/**
 * Espejo de `CommentService._is_transition_allowed` (`app/services/
 * comment_service.py`) para la transición a `resolved` → `pending`
 * ("Reponer"). Es la única transición de comentario que **no** tiene
 * excepción de admin — el backend exige `is_pm` (líder del proyecto)
 * sin más: `if is_pm: return True` es el único camino que cubre la
 * reapertura, a diferencia de mark_addressed/resolve, que sí aceptan
 * al autor/comentarista sin ser líder.
 *
 * No usa `role` a propósito: no hay tier que la compre — ni admin.
 * Deliberadamente NO es `isAdmin || isLead`, aunque ese sea el patrón
 * en el resto del archivo: aquí rompería el espejo con el backend.
 */
export function canReopenComment(access: MaybeAccess, ctx: { isLead: boolean }): boolean {
  return canWriteInTenant(access) && ctx.isLead
}

/** Espejo de `can_manage_project_sources` (`app/core/permissions.py`) —
 *  sube/reemplaza/borra fuentes de proyecto y archivos de documentos en
 *  modo carga (OT-0007 §4.5). Es producir contenido
 *  (`canProduceInProject`): tier de edición y equipo operativo, o admin
 *  que no es miembro. */
export function canManageProjectSources(
  role: MaybeTier,
  access: MaybeAccess,
  ctx: ProjectMembershipContext,
): boolean {
  return canProduceInProject(role, access, ctx)
}

/**
 * Secciones de Equipo & RACI. Desde OT-0058 §4.5 la sección es un dato del
 * miembro (`project_members.section`), no sale de su tier: la licencia es
 * el máximo que puede hacer y la sección es lo que hace en ESE proyecto.
 *
 *  · `operational` — produce documentos y puede ser dueño o líder.
 *  · `extended`    — lee todo y **aprueba**, pero no produce. Admite
 *                    revisores y también editores y admins que en ese
 *                    proyecto solo revisan.
 *  · `stakeholder` — cliente.
 *
 * Espejo de `ALLOWED_SECTIONS` del backend.
 */
export type TeamSection = 'operational' | 'extended' | 'stakeholder'

const SECTION_TIERS: Record<TeamSection, readonly UserTenantRole[]> = {
  operational: ['admin', 'editor'],
  extended: ['admin', 'editor', 'reviewer'],
  stakeholder: ['external'],
}

export function tiersForSection(section: TeamSection): readonly UserTenantRole[] {
  return SECTION_TIERS[section]
}

export function canJoinSection(role: MaybeTier, section: TeamSection): boolean {
  return role != null && (SECTION_TIERS[section] as readonly string[]).includes(role)
}

/** Clave i18n del nombre visible del tier. La copy vive en los locales
 *  (`team.tier.*`), nunca hardcodeada aquí. */
export function tierLabelKey(role: UserTenantRole): string {
  return `tier.${role}`
}

/** Clave i18n de la explicación del tier — la que acompaña a cada opción
 *  en los pickers de invitación y de cambio de tier. Vive junto a
 *  `tierLabelKey` porque el picker es el mismo en los dos diálogos: si la
 *  clave se arma a mano en cada uno, renombrarla obliga a encontrarlos. */
export function tierHelpKey(role: UserTenantRole): string {
  return `invite_modal.role_help_${role}`
}

/**
 * OT-0020 — gating del botón de envío en `ChangeTierDialog`. Sin cambio
 * real que hacer (mismo tier), o falta la confirmación reforzada exigida
 * para promover a `admin` (espejo de `create_invite`).
 */
export function canSubmitTierChange(
  currentTier: UserTenantRole,
  nextTier: UserTenantRole,
  confirmAdmin: boolean,
): boolean {
  if (nextTier === currentTier) return false
  if (nextTier === 'admin' && !confirmAdmin) return false
  return true
}

/**
 * OT-0020 §4.2 invariantes 5 y 6 — cuando el backend rechaza degradar a
 * quien posee documentos o lidera un proyecto, manda la lista en
 * `error.details.artifact_names` o `.project_names`. Extrae esa lista
 * para mostrarla; `null` si el rechazo no trae ninguna (otras invariantes).
 */
export function extractTierChangeBlockers(
  details: Record<string, unknown> | null | undefined,
): string[] | null {
  return (
    (details?.artifact_names as string[] | undefined) ??
    (details?.project_names as string[] | undefined) ??
    null
  )
}

/** Tono de marca del badge por tier. Solo tokens de `Docs/18-BRAND.md`. */
export function tierBadgeTone(role: UserTenantRole): string {
  switch (role) {
    case 'admin':
      return 'bg-brand-soft text-brand'
    case 'external':
      return 'bg-review-soft text-review'
    case 'reviewer':
      return 'bg-surface-muted text-ink-muted'
    default:
      return 'bg-surface-muted text-ink'
  }
}
